import React, { useContext } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input, FormFeedback, Row, Col } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import useCreateReleaseNote from '../../services/useCreateReleaseNote';
import useEditReleaseNote from '../../services/useEditReleaseNote';

const validationSchema = Yup.object({
    versionId: Yup.number().required('Version ID is required'),
    title: Yup.string().required('Title is required'),
    description: Yup.string().required('Description is required'),
    type: Yup.string().required('Type is required'),
    date: Yup.string().required('Date is required'),
    author: Yup.string().required('Author is required'),
    pharmUserOnly: Yup.boolean().required('Pharmacy User Only is required'),
});

function AddReleaseNoteModal({ isModalOpen, setIsModalOpen, selectedReleaseNote, setSelectedReleaseNote, versionId ,setIsVersionModalOpen}) {
    const { addReleaseNote, isAddReleaseNoteLoading } = useCreateReleaseNote(setIsModalOpen);
    const { editReleaseNote, isEditReleaseNoteLoading } = useEditReleaseNote(setIsVersionModalOpen);


    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            return date.toISOString().split('T')[0];
        } catch (e) {
            return '';
        }
    };
    const initialValues = {
        versionId: parseInt(versionId) || '',
        title: selectedReleaseNote?.title || '',
        description: selectedReleaseNote?.description || '',
        type: selectedReleaseNote?.type || '',
        date: formatDateForInput(selectedReleaseNote?.date) || '',
        author: selectedReleaseNote?.author || '',
        pharmUserOnly: selectedReleaseNote?.pharmUserOnly || false,
    };
    const { errors, touched, values, handleChange, handleSubmit,resetForm } = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (formValues) => {
            const formattedDate = formValues.date ? new Date(formValues.date).toISOString() : new Date().toISOString();
            const payload = {
                Id: selectedReleaseNote ? (selectedReleaseNote.id || null) : null,
                VersionId: formValues.versionId,
                Type: formValues.type,
                Author: formValues.author,
                Title: formValues.title,
                Description: formValues.description,
                PharmUserOnly: formValues.pharmUserOnly,
                Date: formattedDate
            };
            if (selectedReleaseNote && payload.Id) {
                editReleaseNote(
                    { payload },
                    {
                        onSuccess: () => {
                            resetForm(); 
                            handleClose();
                        },
                    }
                );
            } else {
                const createPayload = { ...payload };
                delete createPayload.Id;
                addReleaseNote(
                    { payload: createPayload },
                    {
                        onSuccess: () => {
                            resetForm(); 
                            handleClose();
                        },
                    }
                );
            }
        }
    });
    const handleClose = () => {
        setIsModalOpen(false);
        if (setSelectedReleaseNote) {
            setSelectedReleaseNote(null);
        }
    };
    return (
        <Modal isOpen={isModalOpen} toggle={handleClose} size="lg">
            <ModalHeader toggle={handleClose}>
                {selectedReleaseNote ? 'Edit Release Note' : 'Add New Release Note'}
            </ModalHeader>

            <form onSubmit={handleSubmit}>
                <ModalBody>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Title:</Label>
                                <Input
                                    name="title"
                                    value={values.title}
                                    onChange={handleChange}
                                    invalid={touched.title && !!errors.title}
                                />
                                <FormFeedback>{errors.title}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Type:</Label>
                                <Input
                                    name="type"
                                    value={values.type}
                                    onChange={handleChange}
                                    invalid={touched.type && !!errors.type}
                                />
                                <FormFeedback>{errors.type}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label>Description:</Label>
                                <Input
                                    type="textarea"
                                    rows={4}
                                    name="description"
                                    value={values.description}
                                    onChange={handleChange}
                                    invalid={touched.description && !!errors.description}
                                />
                                <FormFeedback>{errors.description}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Date:</Label>
                                <Input
                                    type="date"
                                    name="date"
                                    value={values.date}
                                    onChange={handleChange}
                                    invalid={touched.date && !!errors.date}
                                />
                                <FormFeedback>{errors.date}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Author:</Label>
                                <Input
                                    name="author"
                                    value={values.author}
                                    onChange={handleChange}
                                    invalid={touched.author && !!errors.author}
                                />
                                <FormFeedback>{errors.author}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        type="submit"
                        disabled={isAddReleaseNoteLoading || isEditReleaseNoteLoading}
                    >
                        {(isAddReleaseNoteLoading || isEditReleaseNoteLoading) ? 'Saving...' : 'Save'}
                    </Button>
                </ModalFooter>
            </form>
        </Modal>
    );
}

export default AddReleaseNoteModal;